import React from 'react';
import PropTypes from 'prop-types';
import './OrganisationTable.css';
import ReactCountryFlag from 'react-country-flag';
import { getCodeList } from 'country-list';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const countryListObject = getCodeList();
const CountriesList = Object.entries(countryListObject).map(([code, name]) => ({
  code,
  name,
}));

// Country name mapping for common variations
const countryNameMapping = {
  iran: 'iran, islamic republic of',
  russia: 'russian federation',
  'south korea': 'korea, republic of',
  'north korea': "korea, democratic people's republic of",
  usa: 'united states',
  'united states': 'united states',
  uk: 'united kingdom',
  britain: 'united kingdom',
  england: 'united kingdom',
  uae: 'united arab emirates',
  emirates: 'united arab emirates',
  vietnam: 'viet nam',
  bolivia: 'bolivia, plurinational state of',
  venezuela: 'venezuela, bolivarian republic of',
  tanzania: 'tanzania, united republic of',
  macedonia: 'macedonia, the former yugoslav republic of',
  moldova: 'moldova, republic of',
  syria: 'syrian arab republic',
  laos: "lao people's democratic republic",
  brunei: 'brunei darussalam',
  congo: 'congo, the democratic republic of the',
  'ivory coast': "côte d'ivoire",
  'czech republic': 'czechia',
  'east timor': 'timor-leste',
  swaziland: 'eswatini',
  myanmar: 'myanmar',
  burma: 'myanmar',
};

const OrganisationTable = ({ data = [] }) => {
  const navigate = useNavigate();

  const handleRowClick = (event, id) => {
    navigate(`/admin/organisation/${id}`);
  };

  const renderCountry = countryName => {
    if (!countryName) return '---';

    // First try exact match
    let countryMatch = CountriesList.find(
      country => country.name.toLowerCase() === countryName.toLowerCase()
    );

    // If no exact match, try with mapping
    if (!countryMatch) {
      const mappedName = countryNameMapping[countryName.toLowerCase()];
      if (mappedName) {
        countryMatch = CountriesList.find(
          country => country.name.toLowerCase() === mappedName.toLowerCase()
        );
      }
    }

    return countryMatch ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <ReactCountryFlag
          countryCode={countryMatch.code}
          svg
          style={{ width: '1.2em', height: '1.2em' }}
        />
        <Typography>{countryName}</Typography>
      </Box>
    ) : (
      <Typography>{countryName}</Typography>
    );
  };

  const formatDate = dateStr => {
    if (!dateStr) return '---';

    try {
      // Handle date string in format "DD-MM-YYYY" (already formatted)
      if (dateStr.includes('-') && dateStr.split('-').length === 3) {
        const parts = dateStr.split('-');
        if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
          return dateStr; // Already in correct format
        }
      }

      // Handle ISO date strings from API
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '---';

      return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    } catch (error) {
      return '---';
    }
  };

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        className="organisation-table-container"
        sx={{ borderRadius: '8px' }}
      >
        <Table stickyHeader aria-label="organisation table">
          <TableHead
            sx={{
              backgroundColor: '#EEF4FF',
              '& th': {
                color: 'var(--Text, #14171F)',
                height: '25px',
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontWeight: 500,
                padding: '10px 16px',
              },
            }}
          >
            <TableRow>
              <TableCell>Organization Name</TableCell>
              <TableCell>Organization Email</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Owner Name</TableCell>
              {/* <TableCell align="center">Total Instructors</TableCell> */}
              <TableCell>Created on</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& td': {
                color: 'var(--Text, #14171F)',
                fontFamily: 'Poppins',
                fontSize: '14px',
                padding: '9px 16px',
              },
            }}
          >
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              data.map(row => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={e => handleRowClick(e, row.id)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#E9F1FF !important',
                    },
                  }}
                >
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{renderCountry(row.country)}</TableCell>
                  <TableCell>{row.city}</TableCell>
                  <TableCell>{row.owner}</TableCell>
                  {/* <TableCell align="center">{row.totalInstructors}</TableCell> */}
                  <TableCell>{formatDate(row.createdOn)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

OrganisationTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      country: PropTypes.string,
      owner: PropTypes.string.isRequired,
      totalInstructors: PropTypes.number.isRequired,
      createdOn: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    })
  ),
};

export default OrganisationTable;
